/**
 * Does a recommended step restate a proposed case-file item? (Case round,
 * finding 9: the Do next list on QT-10026 read 16 steps where 11 to 16
 * restated 1 to 10 — the proposed items and Merlin's recommended action
 * are two lists written about the same work, and the brief printed both.)
 *
 * A step restates an item when most of the step's distinctive words (names,
 * numbers, order and lot ids, and the longer ordinary words) appear in the
 * item. Pure logic.
 */

const STOP = new Set(
  'the and for with from that this then them they their have has was were are not but our its into onto out off per via any all who whose when what which once also only just than more most each both'.split(
    ' ',
  ),
);

/** The words that carry a step's meaning. */
export function stepWords(text: string): Set<string> {
  return new Set(
    text
      .toLowerCase()
      .replace(/[*_`#>[\]()]/g, ' ')
      .split(/[^a-z0-9@./$-]+/)
      .map((w) => w.replace(/^[.\-/$]+|[.\-/]+$/g, ''))
      .filter((w) => w.length >= 3 && !STOP.has(w)),
  );
}

/** Share of the step's words that the item also has. */
export function coverage(step: string, item: string): number {
  const s = stepWords(step);
  if (s.size === 0) return 0;
  const i = stepWords(item);
  let hit = 0;
  for (const w of s) {
    if (i.has(w) || [...i].some((x) => x.length >= 5 && w.length >= 5 && (x.startsWith(w) || w.startsWith(x)))) {
      hit += 1;
    }
  }
  return hit / s.size;
}

/** At or over this share, the step restates the item. */
export const RESTATES = 0.45;
/** A step this short ("Pull the retain") says too little to call it a repeat. */
const MIN_WORDS = 4;

/** Index of the item a step restates best, or -1 when it restates none. */
export function restatedItem(step: string, items: string[]): number {
  if (stepWords(step).size < MIN_WORDS) return -1;
  let best = -1;
  let bestScore = RESTATES;
  items.forEach((item, i) => {
    const score = coverage(step, item);
    if (score >= bestScore) {
      best = i;
      bestScore = score;
    }
  });
  return best;
}

/**
 * Who a step names as its owner, when it ends on one in brackets:
 * "… delivers (Emily/Roblyn)." → "Emily/Roblyn". Null otherwise.
 */
export function stepOwner(text: string): string | null {
  const m = /\(([^()]{2,40})\)\s*[.!]?\s*$/.exec(text.trim());
  if (!m) return null;
  const who = m[1].trim();
  // Names, not a note: starts with a capital, no digits.
  return /^[A-Z]/.test(who) && !/\d/.test(who) ? who : null;
}
